import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, forkJoin, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { CurrentUserService } from 'src/app/services/current-user.service';
import { SocialFeedService } from './social-feed.service';
import {
  SocialCommunity,
  SocialGameGroup,
  SocialGroup,
  SocialNotification,
  SocialProfile,
} from '../models/social-feed.model';

/** What the social pages' side rails show. */
export interface SocialHubState {
  profile: SocialProfile | null;
  groups: SocialGroup[];
  gameGroups: SocialGameGroup[];
  communities: SocialCommunity[];
  hasMoreCommunities: boolean;
  /** Communities you could join (for the "Discover" rail card). */
  suggested: SocialCommunity[];
  inviteCount: number;
  unreadNotifications: number;
  unreadMessages: number;
  notifications: SocialNotification[];
  loaded: boolean;
}

const EMPTY: SocialHubState = {
  profile: null,
  groups: [],
  gameGroups: [],
  communities: [],
  hasMoreCommunities: false,
  suggested: [],
  inviteCount: 0,
  unreadNotifications: 0,
  unreadMessages: 0,
  notifications: [],
  loaded: false,
};

/**
 * Everything the social pages' rails need, loaded once and shared, so moving
 * between social pages doesn't fetch the same lists again (each call is slow
 * against a remote DB). One 30-second poll keeps the unread counts fresh, and
 * only while a social page is on screen (see attach()). Pages call refresh()
 * after something that changes these lists (joining a community, say).
 */
@Injectable({ providedIn: 'root' })
export class SocialHubService implements OnDestroy {
  private static readonly POLL_MS = 30_000;
  private readonly state$ = new BehaviorSubject<SocialHubState>(EMPTY);
  readonly state: Observable<SocialHubState> = this.state$.asObservable();

  private userId: number | null = null;
  private attached = 0;
  private timer: number | null = null;
  private lastUnread = -1;
  private loading = false;

  constructor(private social: SocialFeedService, currentUser: CurrentUserService) {
    currentUser.currentUser.subscribe((user) => {
      const id = user?.id ?? null;
      if (id !== this.userId) {
        // Signed out or another account: forget the previous person's lists.
        this.userId = id;
        this.lastUnread = -1;
        this.state$.next(EMPTY);
      }
    });
  }

  get snapshot(): SocialHubState {
    return this.state$.value;
  }

  /** A social page came on screen: load once, start the poll. Returns the detach function. */
  attach(): () => void {
    this.attached += 1;
    if (!this.snapshot.loaded && this.userId != null) {
      this.refresh();
    }
    if (this.timer === null) {
      this.timer = window.setInterval(() => this.poll(), SocialHubService.POLL_MS);
      document.addEventListener('visibilitychange', this.onVisible);
    }
    return () => {
      this.attached = Math.max(0, this.attached - 1);
      if (this.attached === 0) this.stopPolling();
    };
  }

  private onVisible = () => {
    if (!document.hidden) this.poll();
  };

  private stopPolling(): void {
    if (this.timer !== null) {
      window.clearInterval(this.timer);
      this.timer = null;
    }
    document.removeEventListener('visibilitychange', this.onVisible);
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private patch(changes: Partial<SocialHubState>): void {
    this.state$.next({ ...this.state$.value, ...changes });
  }

  /** Reload every list. Each part shows as soon as it arrives (the DB is
   * often slow), and one failing leaves the others. */
  refresh(): void {
    if (this.loading || this.userId == null) return;
    this.loading = true;
    const part = <T>(obs: Observable<T>, fallback: T, apply: (v: T) => Partial<SocialHubState>) =>
      obs.pipe(
        catchError(() => of(fallback)),
        tap((v) => this.patch(apply(v)))
      );
    forkJoin([
      part(this.social.getMyProfile(), null as SocialProfile | null, (profile) => ({ profile })),
      part(this.social.getActivityUnreadCounts(), { notifications: 0, messages: 0 }, (c) => {
        this.lastUnread = c.notifications;
        return { unreadNotifications: c.notifications, unreadMessages: c.messages };
      }),
      part(this.social.getNotifications().pipe(map((r) => r.results)), [] as SocialNotification[], (notifications) => ({ notifications })),
      part(this.social.getCommunities({ scope: 'mine' }), null, (mine) => ({
        communities: mine?.results ?? [],
        hasMoreCommunities: !!mine?.next,
      })),
      part(this.social.getMyCommunityInvites().pipe(map((r) => r.count)), 0, (inviteCount) => ({ inviteCount })),
      part(this.social.getGroups(), [] as SocialGroup[], (groups) => ({ groups })),
      part(this.social.getGameGroups(), [] as SocialGameGroup[], (gameGroups) => ({ gameGroups })),
      part(this.social.getCommunities({ scope: 'all' }), null, (all) => ({
        suggested: (all?.results ?? []).filter((c) => !c.my_role).slice(0, 4),
      })),
    ]).subscribe(() => {
      this.loading = false;
      this.patch({ loaded: true });
    });
  }

  /** Only the parts that change when you join / leave / are invited. */
  refreshCommunities(): void {
    forkJoin({
      mine: this.social.getCommunities({ scope: 'mine' }).pipe(catchError(() => of(null))),
      suggested: this.social.getCommunities({ scope: 'all' }).pipe(catchError(() => of(null))),
      invites: this.social.getMyCommunityInvites().pipe(map((r) => r.count), catchError(() => of(this.snapshot.inviteCount))),
    }).subscribe((r) => {
      this.patch({
        communities: r.mine?.results ?? this.snapshot.communities,
        hasMoreCommunities: r.mine ? !!r.mine.next : this.snapshot.hasMoreCommunities,
        suggested: r.suggested ? r.suggested.results.filter((c) => !c.my_role).slice(0, 4) : this.snapshot.suggested,
        inviteCount: r.invites,
      });
    });
  }

  refreshProfile(): void {
    this.social.getMyProfile().pipe(catchError(() => of(null))).subscribe((profile) => profile && this.patch({ profile }));
  }

  private poll(): void {
    if (document.hidden || this.userId == null || !this.snapshot.loaded) return;
    this.social.getActivityUnreadCounts().subscribe({
      next: ({ notifications, messages }) => {
        this.patch({ unreadNotifications: notifications, unreadMessages: messages });
        if (notifications !== this.lastUnread) {
          this.lastUnread = notifications;
          this.loadNotifications();
        }
      },
      error: () => undefined, // try again next time
    });
  }

  loadNotifications(): void {
    this.social.getNotifications().subscribe({
      next: (page) => this.patch({ notifications: page.results }),
      error: () => undefined,
    });
  }

  markNotificationRead(n: SocialNotification): void {
    if (n.is_read) return;
    n.is_read = true;
    this.lastUnread = Math.max(0, this.snapshot.unreadNotifications - 1);
    this.patch({ unreadNotifications: this.lastUnread, notifications: [...this.snapshot.notifications] });
    this.social.markNotificationRead(n.id).subscribe({ error: () => undefined });
  }

  markAllNotificationsRead(): void {
    if (!this.snapshot.unreadNotifications) return;
    this.lastUnread = 0;
    this.patch({
      unreadNotifications: 0,
      notifications: this.snapshot.notifications.map((n) => ({ ...n, is_read: true })),
    });
    this.social.markAllNotificationsRead().subscribe({ error: () => undefined });
  }

  setUnreadMessages(count: number): void {
    this.patch({ unreadMessages: count });
  }

  /** Where a notification points (shared by every place that lists them). */
  static notificationTarget(n: SocialNotification): { commands: any[]; queryParams?: Record<string, string | number> } | null {
    if (n.notif_type === 'follow' && n.actor) return { commands: ['/social-profile', n.actor.id] };
    if (n.post != null) return { commands: ['/social-post', n.post] };
    if (n.group != null) return { commands: ['/social-group', n.group] };
    if (n.game_group != null) return { commands: ['/social-game-group', n.game_group] };
    if (n.notif_type === 'community_invite') return { commands: ['/communities'], queryParams: { tab: 'invites' } };
    if (n.community_slug) {
      return {
        commands: ['/communities', n.community_slug],
        queryParams: n.notif_type === 'community_request' ? { tab: 'requests' } : undefined,
      };
    }
    return null;
  }
}
